import { serialize } from "object-to-formdata"
import { message } from "antd"
import { createPaymentIntent, capturePayment, resetExperienceBookingData } from "store/experiences"
import dayjs from "dayjs"
import { saveGuideBooking, setClientId } from "store/bookings"
import { setSelectedPaymentMethodId } from "store/user"
import { initialValues, PAYMENT_OPTIONS, BOOKING_SOURCE } from "./constants"
import { buildAddOnSelectionsAttributes } from "components/bookings/helpers"
import { buildBreakdownFromPaymentSummary, computeGuestSubtotalFromPaymentSummary } from "./helpers/calculateBookingTotal"
import { paymentProvider } from "ui/Booking/Details/Body/PaymentSummary/Modals/TakePayment/TakePaymentForm/constants"
import { calculatePaymentBreakdown } from "utils/bookingCalculations"

export const handleNameKeyDown = (e) => {
  const key = e.key
  const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"]

  if (/^[A-Za-z]$/.test(key) || allowedKeys.includes(key)) {
    // allowed, do nothing
    return
  }
  e.preventDefault()
}

// Utility function to handle trip booking submission

const buildClientAttributes = (values, client) => {
  // If existing client, only send phone if missing on selection
  if (values?.isExistingClient) {
    if (values?.existing_client?.phone_number) {
      return {}
    }
    return { phone_number: values?.phone_number?.fullNumber, country_code: values?.phone_number?.countryCode }
  }
  // If client object exists, only send phone if missing on user
  if (client?.user_id) {
    if (client?.user?.phone_number) {
      return {}
    }
    return { phone_number: values?.phone_number?.fullNumber, country_code: values?.phone_number?.countryCode }
  }
  // For new clients, send all details
  return {
    first_name: values?.firstName,
    last_name: values?.lastName,
    email: values?.email ?? "",
    phone_number: values?.phone_number?.fullNumber,
    country_code: values?.phone_number?.countryCode,
    full_name: values?.existing_client?.label,
    ...(values?.email ? {} : { no_email: true })
  }
}

const getPaymentType = (values, booking) => {
  const { deposit_later, deposit_cash, full_now, full_cash, full_link, deposit_link, deposit_link_in_cash, pay_later } = PAYMENT_OPTIONS
  // Define pay later eligible payment options
  const PAY_LATER_OPTIONS = [full_now, pay_later]
  const PAY_LINK_OPTIONS = [full_link]

  // Handle pay later case
  if (PAY_LATER_OPTIONS.includes(booking?.payment_option) && values.collectPayment === "no") {
    return pay_later
  }
  if (PAY_LINK_OPTIONS.includes(booking?.payment_option) && values.collectPayment === "no") {
    return full_link
  }

  if (values?.paymentType === "paypalLink") {
    if (values?.depositOnly) {
      return values?.paymentOption === deposit_cash ? deposit_link_in_cash : deposit_link
    }
    return full_link
  }

  // Handle card payments
  if (values?.paymentType === "card") {
    // Prefer the live form selection (radio) over the saved booking value, otherwise editing a
    // booking always re-sends its original payment_option and toggling the deposit radio is ignored.
    switch (values?.paymentOption || booking?.payment_option) {
      case deposit_link_in_cash:
      case deposit_cash:
        return deposit_cash
      case deposit_link:
      case deposit_later:
        return deposit_later
      default:
        return full_now
    }
  }

  // Handle cash payment
  if (values?.paymentType === "cash") {
    return full_cash
  }

  // Handle link payment with deposit
  if (values?.paymentType === "link") {
    if (values?.depositOnly) {
      return values?.paymentOption === deposit_cash ? deposit_link_in_cash : deposit_link
    }
    return full_link
  }

  // Default to the existing payment option if none of the above conditions match
  return values?.paymentOption
}

const buildTripPayload = ({
  values,
  bookingSelections,
  timeRange: { start_time, end_time },
  paymentType,
  clientInfo: { includeClientAttributes, clientId, client, is_refund },
  selectedPaymentMethodId,
  isEdit,
  isExperienceChanged,
  depositType,
  paymentProvider,
  appliedPromos = [],
  existingBookingAddOns = []
}) => {
  let depositTypeToSend = "default"
  if (isEdit) {
    depositTypeToSend = depositType
  }
  if (values?.customDepositAmount) {
    depositTypeToSend = "custom"
  }
  /* Payload Calculation with Waterfall Logic */
  const priceData = bookingSelections?.tripLength?.price || {}
  const { pricing_type, included_guests, max_guests } = priceData

  const mainCounts = bookingSelections?.counts?.main || {}
  const extraCounts = bookingSelections?.counts?.extra || {}

  const totalCounts = {
    adults: (Number(mainCounts.adults) || 0) + (Number(extraCounts.adults) || 0),
    children: (Number(mainCounts.children) || 0) + (Number(extraCounts.children) || 0),
    infants: (Number(mainCounts.infants) || 0) + (Number(extraCounts.infants) || 0),
    passengers: (Number(mainCounts.passengers) || 0) + (Number(extraCounts.passengers) || 0)
  }

  let finalIncluded = { adults: 0, children: 0, infants: 0, passengers: 0 }
  let finalExtra = { adults: 0, children: 0, infants: 0, passengers: 0 }

  if (pricing_type === "charge_flat_rate") {
    const limit = included_guests ?? max_guests ?? 0
    let remaining = limit

    // Adults First
    const adultsIn = Math.min(totalCounts.adults, remaining)
    remaining = Math.max(0, remaining - adultsIn)
    finalExtra.adults = Math.max(0, totalCounts.adults - adultsIn)
    finalIncluded.adults = adultsIn

    // Children
    const childrenIn = Math.min(totalCounts.children, remaining)
    remaining = Math.max(0, remaining - childrenIn)
    finalExtra.children = Math.max(0, totalCounts.children - childrenIn)
    finalIncluded.children = childrenIn

    // Infants
    const infantsIn = Math.min(totalCounts.infants, remaining)
    finalExtra.infants = Math.max(0, totalCounts.infants - infantsIn)
    finalIncluded.infants = infantsIn

    // Passengers (Always Extra in Flat Rate)
    finalExtra.passengers = totalCounts.passengers
    finalIncluded.passengers = 0
  } else {
    // Per Person or other types: All in Main
    finalIncluded = { ...totalCounts }
    finalExtra = { adults: 0, children: 0, infants: 0, passengers: 0 }
  }

  const payload = {
    trip_id: values?.experience?.key,
    accessibility_enabled: values?.accessibility_required,
    accessibility_notes: values?.accessibility_notes,
    note: values?.notes,
    booked_date: bookingSelections?.date,
    adult_count: finalIncluded.adults,
    child_count: finalIncluded.children,
    infant_count: finalIncluded.infants,
    passenger_count: finalIncluded.passengers,
    start_time,
    end_time,
    trip_price_id: bookingSelections?.tripLength?.key,
    extra_adult_count: finalExtra.adults,
    extra_child_count: finalExtra.children,
    extra_infant_count: finalExtra.infants,
    extra_passenger_count: finalExtra.passengers,
    deposit_percentage: values?.customDepositAmount || values?.depositPercent,
    deposit_type: depositTypeToSend,
    payment_option: paymentType,
    record_now: getRecordNowValue(values?.collectPayment),
    record_amount: values?.recordPayment,
    payment_method_id: selectedPaymentMethodId,
    refund_by: values?.refundBy,
    is_refund,
    payment_provider: paymentProvider,
    booking_source: BOOKING_SOURCE.manual,
    promo_codes: appliedPromos.map((p) => p.code),
    booking_add_ons_attributes: buildAddOnSelectionsAttributes(bookingSelections?.addOns, existingBookingAddOns)
  }

  // Tell the backend whether the experience/trip was swapped while editing an existing booking, so
  // it can re-point the booking to the new trip (and recalc against it) rather than editing the
  // original in place. Sent on edit only — a new booking has no existing trip to change.
  if (isEdit) {
    payload["allow_trip_change"] = isExperienceChanged
  }

  if (clientId) {
    payload["client_id"] = clientId
  }

  if (includeClientAttributes) {
    const clientAttrs = buildClientAttributes(values, client)
    if (clientAttrs && Object.keys(clientAttrs).length > 0) {
      payload["client_attributes"] = clientAttrs
    }
  }

  // Subtotal adjustment (create-screen only): the guide can negotiate a custom base subtotal.
  // `adjusted_subtotal` is the new base (excludes add-ons); the backend recalculates all fees
  // and the total on it. Send it only when actually adjusted; post-creation editing is a
  // different flow and is not supported here.
  const adjustedSubtotal = Number(values?.adjustedPrice)
  if (!isEdit && values?.adjustedPrice != null && values?.adjustedPrice !== "" && !isNaN(adjustedSubtotal) && adjustedSubtotal > 0) {
    payload["adjusted_subtotal"] = adjustedSubtotal
    if (values?.adjustmentReason) {
      payload["adjustment_reason"] = values.adjustmentReason
    }
  }

  return payload
}

const getRecordNowValue = (collectPaymentValue) => {
  if (collectPaymentValue === "yes") return true
  if (collectPaymentValue === "no") return false
  return null
}

export const submitTripBooking = async ({
  form,
  dispatch,
  stripe,
  bookingSelections,
  selectedPaymentMethodId,
  bookingUuid,
  clientId,
  isBookAgain,
  isEdit,
  client,
  booking,
  appliedPromos = []
}) => {
  let bookingRes = null
  try {
    // Step 1: Validate currently mounted fields, then collect all preserved values
    await form.validateFields()
    const values = form.getFieldsValue(true)
    const [start_time, end_time] = bookingSelections?.timeSlot?.split(" - ") || []
    const paymentType = getPaymentType(values, booking)
    // Detect an experience swap during edit: the selected experience's uuid differs from the
    // booking's original trip. Book-again creates a fresh booking, so it's not a "change".
    const isExperienceChanged =
      !!isEdit && !isBookAgain && !!booking?.trip?.uuid && values?.experience?.value !== booking.trip.uuid
    // A refund only applies when the edited total drops below what the client has already paid.
    // is_refund must reflect that — NOT the collect-payment flag (which defaults to "yes" and made
    // is_refund true on every edit, including price increases / experience swaps that cost more).
    // The <24h flow lets the guide decline the refund via collectPayment="no", so honour that opt-out.
    const { refundDueNow = 0 } = isEdit ? calculatePaymentBreakdown({ booking, bookingSelections }) : {}
    const is_refund = refundDueNow > 0 && values?.collectPayment !== "no"
    // Step 2: Build and serialize payload
    const clientAttribute = (bookingUuid && isBookAgain) || !bookingUuid
    const payload = buildTripPayload({
      values,
      bookingSelections,
      timeRange: { start_time, end_time },
      paymentType,
      paymentProvider: values?.paymentType ? paymentProvider[values.paymentType] : null,
      clientInfo: {
        includeClientAttributes: clientAttribute,
        clientId,
        client,
        is_refund
      },
      selectedPaymentMethodId,
      isEdit,
      isExperienceChanged,
      depositType: booking?.deposit_type,
      appliedPromos,
      existingBookingAddOns: isEdit ? booking?.booking_add_ons : []
    })

    const { promo_codes, ...restPayload } = payload
    const formData = serialize({ trip_booking: restPayload }, { indices: true })
    promo_codes?.forEach((code) => formData.append("trip_booking[promo_codes][]", code))
    // Step 3: Save booking
    bookingRes = await dispatch(saveGuideBooking(bookingUuid, formData, isBookAgain))

    // Step 4: Process payment if needed
    const newBookingUuid = bookingRes?.uuid || bookingRes?.data?.uuid
    const bookingId = bookingRes.id || bookingRes?.data?.id
    const bookingGuestToken = bookingRes?.guest_token || bookingRes?.data?.guest_token
    
    if (selectedPaymentMethodId && !isEdit) {
      const paymentRes = await dispatch(createPaymentIntent(bookingId, bookingGuestToken))
      if (!paymentRes?.client_secret) {
        throw new Error("Payment intent creation failed. No client secret returned.")
      }

      // Step 5: Confirm payment
      const result = await stripe.confirmCardPayment(paymentRes.client_secret, {
        payment_method: selectedPaymentMethodId
      })

      if (result?.error) {
        const error = new Error(result.error.message || "Card payment failed.")
        error.retryBookingUuid = newBookingUuid
        throw error
      }

      const paymentIntentId = result?.paymentIntent?.id
      if (!paymentIntentId) {
        throw new Error("No paymentIntent ID received from Stripe.")
      }

      // Step 6: Capture payment
      const captureRes = await dispatch(capturePayment(bookingId, paymentRes?.income?.id))
      if (captureRes?.error) {
        throw new Error("Payment capture failed.")
      }
    }

    // Success
    message.success(isEdit ? "Booking updated successfully!" : "Booking added successfully!")
    return
  } catch (err) {
    console.error("Booking Submission Error:", err)
    const newBookingUuid = bookingRes?.uuid || bookingRes?.data?.uuid

    // Throw error with retry UUID if available
    const error = new Error(err.message)
    if (newBookingUuid) {
      error.retryBookingUuid = newBookingUuid
    }
    throw error
  }
}

export const validatePaymentAmount = (value, { maxAmount, isRefund, t } = {}) => {
  // Handle empty/undefined/null case first
  if (value === "" || value === null || value === undefined) {
    return Promise.reject(t("booking.validation.record_payment_required"))
  }

  const numericValue = Number.parseFloat(value)

  // Check for invalid number
  if (Number.isNaN(numericValue)) {
    return Promise.reject(t("booking.validation.invalid_number"))
  }

  // Check for non-positive numbers
  if (numericValue <= 0) {
    return Promise.reject(t("booking.validation.amount_positive"))
  }

  // Check against maximum amount if provided
  if (maxAmount !== undefined && numericValue > maxAmount) {
    return isRefund ? Promise.reject(t("booking.validation.exceeds_refund")) : Promise.reject(t("booking.validation.exceeds_balance"))
  }

  // Validation passed
  return Promise.resolve()
}

// Detects whether the user has made any user-visible change to the booking inputs
// (guest counts, trip duration, or add-ons) compared to the booking's saved state.
// Used to skip recalculation on the initial edit view — the booking's stored total is the
// source of truth until the user actually changes something, so recomputing can only introduce
// drift (e.g. when experience settings have changed since the booking was created, or when
// data hasn't fully loaded yet).
export const hasNoUserBookingChanges = (bookingSelections, booking) => {
  if (!booking || !bookingSelections) return false

  const originalCounts = {
    adults: (booking?.adult_count || 0) + (booking?.extra_adult_count ?? booking?.extra_adult?.count ?? 0),
    children: (booking?.child_count || 0) + (booking?.extra_child_count ?? booking?.extra_child?.count ?? 0),
    infants: (booking?.infant_count || 0) + (booking?.extra_infant_count ?? booking?.extra_infant?.count ?? 0),
    passengers: (booking?.passenger_count || 0) + (booking?.extra_passenger_count ?? booking?.extra_passenger?.count ?? 0)
  }
  const main = bookingSelections.counts?.main || {}
  const extra = bookingSelections.counts?.extra || {}
  const guestCountChanged = ["adults", "children", "infants", "passengers"].some(
    (key) => (Number(main[key]) || 0) + (Number(extra[key]) || 0) !== originalCounts[key]
  )

  const originalAddOnsTotal = (booking.booking_add_ons || []).reduce((sum, item) => sum + Number(item.total_price || 0), 0)
  const addOnsChanged = Math.abs(Number(bookingSelections.addOnsTotal || 0) - originalAddOnsTotal) > 0.01

  // Negotiated-subtotal bookings pin the base to the adjusted price, so date/duration edits never
  // change the total and are treated as "no change" (the "price not changed" view is shown for them).
  // Guest and add-on edits still count as changes: guest edits so the breakdown reflects the new
  // counts (base stays the adjusted price), add-on edits because they change the fees/total.
  if (booking?.payment_summary?.subtotal_adjusted) {
    return !guestCountChanged && !addOnsChanged
  }

  // A date change can move the trip onto/off a day_adjustment (e.g. weekend surcharge) or a
  // seasonal_adjustment (e.g. a holiday range), so it must trigger a recalculation even if
  // guest counts/duration/add-ons are unchanged.
  if (bookingSelections.date && bookingSelections.date !== booking.booked_date) return false

  if (guestCountChanged) return false

  if (bookingSelections.tripLength?.value !== booking.selected_trip_duration) return false

  if (addOnsChanged) return false

  return true
}

// Maps a booking's saved payment_option to the form's `paymentType` vocabulary.
// Used as a fallback during edit-mode recalculation: the form's `paymentType` can be
// transiently undefined (the PaymentMethod component, which authoritatively sets it, only
// mounts on Step 2 — and not at all when a refund is shown). A calc with an undefined
// paymentType silently drops the platform fee and card processing fee, lowering the total and
// producing a phantom "refund due". Falling back to the booking's payment type keeps the fees.
const EDIT_PAYMENT_OPTION_TO_TYPE = {
  [PAYMENT_OPTIONS.full_now]: "card",
  [PAYMENT_OPTIONS.deposit_later]: "card",
  [PAYMENT_OPTIONS.deposit_cash]: "card",
  [PAYMENT_OPTIONS.deposit_link]: "card",
  [PAYMENT_OPTIONS.deposit_link_in_cash]: "card",
  [PAYMENT_OPTIONS.pay_later]: "card",
  [PAYMENT_OPTIONS.full_cash]: "cash",
  [PAYMENT_OPTIONS.full_link]: "link",
  [PAYMENT_OPTIONS.deposit_amount_by_link]: "link"
}

export const deriveBookingPaymentType = (booking) => EDIT_PAYMENT_OPTION_TO_TYPE[booking?.payment_option] || "link"

// Returns the payment type to use for a recalculation. The form value (an explicit user choice)
// always wins; only when it is missing do we fall back to the booking's saved payment type, and
// only in edit mode (a new booking has no booking to fall back to).
export const resolveEditPaymentType = (paymentType, booking, isEdit) =>
  paymentType || (isEdit ? deriveBookingPaymentType(booking) : paymentType)

export const populateFormWithBooking = async ({ booking, bookingUuid, dispatch, form, isBookAgain }) => {
  if (bookingUuid && booking) {
    // do nothing
  } else {
    form.setFieldsValue(initialValues)
    return
  }

  const client = booking.client
  const trip = booking.trip

  // Subtotal adjustment (set at creation): carry the negotiated base into edit mode so the same
  // adjusted price drives the display and calculation. It's read-only here — the guide can't
  // re-adjust while editing — but must persist until (a future flow adds) a new adjusted price.
  const paymentSummary = booking?.payment_summary ?? {}
  const isSubtotalAdjusted = !!paymentSummary.subtotal_adjusted && paymentSummary.adjusted_subtotal != null
  const adjustedBaseAmount = isSubtotalAdjusted ? Number(paymentSummary.adjusted_subtotal) : null
  const computedBaseAmount = computeGuestSubtotalFromPaymentSummary(paymentSummary)
  const originalBaseAmount = Number(paymentSummary.original_subtotal) || computedBaseAmount

  const paymentOptionMap = {
    full_amount_today: "card",
    deposit_and_balance_later: "card",
    deposit_and_balance_in_cash: "card",
    full_amount_in_cash_on_day: "cash",
    full_amount_by_link: "link",
    deposit_by_link_and_balance_later: "link",
    deposit_by_link_and_balance_in_cash: "link",
    pay_later: "card"
  }
  const { deposit_later, deposit_cash, deposit_link, deposit_link_in_cash } = PAYMENT_OPTIONS

  const paymentType = paymentOptionMap[booking?.payment_option] || "link"
  const depositPercent = [deposit_later, deposit_cash, deposit_link, deposit_link_in_cash].includes(booking?.payment_option)
  dispatch(setSelectedPaymentMethodId(booking?.last_selected_payment_method))
  // user_id, not the client record id: every other writer of this field stores user_id (the
  // existing-client dropdown, and the booking-detail screen), and it is what the backend reads as
  // client_id. Falls back to `id` only if the payload has no user_id, preserving the old value.
  dispatch(setClientId(client?.user_id ?? client?.id))
  await form.setFieldsValue({
    firstName: client.first_name,
    lastName: client.last_name,
    email: client.email,
    phone_number: { fullNumber: client?.phone_number, countryCode: client?.country_code },
    experience: {
      label: trip?.title,
      value: trip?.uuid,
      key: trip?.id
    },
    date: dayjs(booking.booked_date),
    duration: booking?.selected_trip_duration,
    time: `${booking?.start_time} - ${booking?.end_time}`,
    accessibility_required: booking?.accessibility_enabled,
    accessibility_notes: booking?.accessibility_notes,
    depositOnly: depositPercent,
    depositPercent: depositPercent ? booking?.deposit_percentage : null,
    paymentOption: booking?.payment_option,
    paymentType,
    notes: booking?.note,
    collectPayment: booking?.collect_payment || "yes",
    adjustedPrice: adjustedBaseAmount,
    adjustmentReason: isSubtotalAdjusted ? paymentSummary.subtotal_adjustment_reason : null
  })

  if (isBookAgain) {
    dispatch(resetExperienceBookingData())
    // Book-again is a fresh booking — don't carry the source booking's negotiated subtotal; the
    // guide re-adjusts from scratch on the create screen.
    form.setFieldsValue({ date: null, duration: "", time: "", depositOnly: false, depositPercent: null, adjustedPrice: null, adjustmentReason: null })
    return null
  }

  const existingAddOns = (booking.booking_add_ons || []).reduce((acc, item) => {
    const id = item.add_on_id
    if (id) acc[id] = item.quantity
    return acc
  }, {})
  const existingAddOnsTotal = (booking.booking_add_ons || []).reduce((sum, item) => sum + Number(item.total_price || 0), 0)

  return {
    date: booking.booked_date,
    timeSlot: `${booking?.start_time} - ${booking?.end_time}`,
    tripLength: {
      key: booking?.trip_schedule?.id,
      value: booking?.selected_trip_duration,
      price: booking?.trip_schedule
    },
    counts: {
      main: {
        adults: (booking?.adult_count || 0) + (booking?.extra_adult_count ?? booking?.extra_adult?.count ?? 0),
        children: (booking?.child_count || 0) + (booking?.extra_child_count ?? booking?.extra_child?.count ?? 0),
        infants: (booking?.infant_count || 0) + (booking?.extra_infant_count ?? booking?.extra_infant?.count ?? 0),
        passengers: (booking?.passenger_count || 0) + (booking?.extra_passenger_count ?? booking?.extra_passenger?.count ?? 0)
      },
      extra: { adults: 0, children: 0, infants: 0, passengers: 0 }
    },
    addOns: existingAddOns,
    addOnsTotal: existingAddOnsTotal,
    amount: Number(booking?.payment_summary?.total_price) || 0,
    // When the subtotal was negotiated at creation, baseAmount is the adjusted (effective) base and
    // originalBaseAmount is the pre-adjustment computed subtotal — mirroring the create-screen fields
    // so PriceBreakdown can render the struck-through original alongside the adjusted price.
    baseAmount: isSubtotalAdjusted ? adjustedBaseAmount : computedBaseAmount,
    originalBaseAmount,
    breakdown: buildBreakdownFromPaymentSummary(booking?.payment_summary)
  }
}
